import type { TWasmMapHover } from "../wasm";
import type CursorManager from "~/util/cursorManager";
import type Wasm from "../wasm";
import { createMarkerPopup } from "~/ui/map/markerpopup";
import { computePosition, shift, flip } from "@floating-ui/dom";
import { nearestPointOnLine } from "@turf/nearest-point-on-line";
import { lineString, point } from "@turf/helpers";
import { cleanCoords } from "@turf/clean-coords";
import { length } from "@turf/length";
import { stringToCoords } from "~/util/mirek";
import { standardDistanceFormat } from "~/util/format";
import { createRouteSummaryPopup } from "~/ui/map/routesummarypopup";

const SHOW_POPUP_DELAY = 500;
const HIDE_POPUP_DELAY = 250;
/** DOM element pouzivany pro popup nad markerem v mape */
const markerPopup = createMarkerPopup();
/** timeout pro zobrazeni popupu */
let popupShowTimeout: number = 0;
/** timeout pro schovani popupu */
let popupHideTimeout: number = 0;

export function updatePopupPosition(clientX: number, clientY: number) {
	if (markerPopup.container.isConnected) {
		computePosition({
			getBoundingClientRect() {
				return {
					width: 0,
					height: 0,
					x: clientX,
					y: clientY,
					top: clientY,
					left: clientX,
					right: clientX,
					bottom: clientY,
				};
			},
		}, markerPopup.container, {
			placement: "bottom-start",
			middleware: [
				flip({
					crossAxis: "alignment",
					fallbackAxisSideDirection: "end",
				}),
				shift({
					crossAxis: true,
				}),
			],
		}).then(({ x, y }) => {
			Object.assign(markerPopup.container.style, {
				left: `${x}px`,
				top: `${y}px`,
			});
		});
	}
}

export function showPopup({ clientX, clientY, contentBuilder, mode }: { clientX: number, clientY: number, contentBuilder: () => Promise<DocumentFragment>, mode: "marker" | "geometry" }) {
	// pokud se melo ukazat neco jineho, tak ted uz ne
	popupShowTimeout && clearTimeout(popupShowTimeout);
	// pokud se melo schovavat, tak ted uz ne
	popupHideTimeout && clearTimeout(popupHideTimeout);
	popupHideTimeout = 0;
	// nastavime timeout na zobrazeni popupu
	popupShowTimeout = window.setTimeout(async () => {
		// pokud ve chvili zobrazeni mame neco schovat, tak to schovani zrusime
		popupHideTimeout && clearTimeout(popupHideTimeout);
		// vynulujeme si timeouty
		popupShowTimeout = 0;
		popupHideTimeout = 0;
		// skutecne ukazeme popup
		markerPopup.card.innerHTML = "";
		markerPopup.card.appendChild(await contentBuilder());
		markerPopup.container.classList[mode === "geometry" ? "add" : "remove"]("ui-marker-popup--geometry");
		document.body.appendChild(markerPopup.container);
		updatePopupPosition(clientX, clientY);

		// navesime ruzne posluchace pro schovani popupu
		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		markerPopup.card.addEventListener("click", hidePopup);
		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		document.querySelector("canvas.wasm-canvas").addEventListener("pointerdown", hidePopup, { once: true });
		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		window.addEventListener("resize", hidePopup, { once: true });
		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		window.addEventListener("wheel", hidePopup, { once: true });
	}, SHOW_POPUP_DELAY);
}

export function hidePopup() {
	!popupHideTimeout && (popupHideTimeout = window.setTimeout(() => {
		// pokud se melo ukazat neco jineho, tak ted uz ne
		popupShowTimeout && clearTimeout(popupShowTimeout);
		// skutecne schovame popup
		markerPopup.container.remove();
	}, HIDE_POPUP_DELAY));
}

markerPopup.card.addEventListener("pointerenter", () => {
	popupHideTimeout && clearTimeout(popupHideTimeout);
	popupHideTimeout = 0;
	popupShowTimeout && clearTimeout(popupShowTimeout);
	popupShowTimeout = 0;
});
markerPopup.card.addEventListener("pointerleave", () => {
	hidePopup();
});

/** reference na popup pro shrnuti trasy */
let summaryGeometryPopup: ReturnType<typeof createRouteSummaryPopup> = null;
/** reference na GEOJson linii */
let summaryGeometryLine: ReturnType<typeof lineString> = null;
/** celkova delka GEOJson geometrie */
let summaryGeometryTotalLength: number = 0;

export function setRouteSummaryPopup(strPoints: string, contactX: number, contactY: number, wasm: Wasm) {
	// pokud jeste neni vytvorena GEOJson geometrie, tak ji vyrobime
	if (!summaryGeometryLine) {
		summaryGeometryLine = cleanCoords(lineString(stringToCoords(strPoints)));
	}

	// pokud jeste neni spocitana delka GEOJson geometrie, tak ji spocitame
	if (!summaryGeometryTotalLength) {
		summaryGeometryTotalLength = length(summaryGeometryLine, { units: "meters" });
	}

	const contactPoint = wasm.coordsFromScreenPosition(contactX, contactY);
	const nearest = nearestPointOnLine(summaryGeometryLine, point([contactPoint.x, contactPoint.y]), { units: "meters" });

	if (!summaryGeometryPopup) {
		summaryGeometryPopup = createRouteSummaryPopup();
		document.body.appendChild(summaryGeometryPopup.container);
	}

	const fromObject = standardDistanceFormat(nearest.properties.location.toString());
	const toObject = standardDistanceFormat(((summaryGeometryTotalLength - nearest.properties.location)).toString());

	summaryGeometryPopup.fromStart.innerHTML = `<strong>${fromObject.distance}</strong>&nbsp;${fromObject.unit}`;
	summaryGeometryPopup.toFinish.innerHTML = `<strong>${toObject.distance}</strong>&nbsp;${toObject.unit}`;
	summaryGeometryPopup.container.style.setProperty("left", `${contactX}px`);
	summaryGeometryPopup.container.style.setProperty("bottom", `${window.innerHeight - contactY}px`);
}

export function unsetRouteSummaryPopup() {
	summaryGeometryPopup?.container.remove();
	summaryGeometryPopup = null;
	summaryGeometryLine = null;
	summaryGeometryTotalLength = 0;
}

/** reference na aktualni kurzor */
let hoverCursor: object = null;

export function useCursor(cursor: string, data: TWasmMapHover, cursorManager: CursorManager) {
	if (data.type === "enter") {
		if (hoverCursor) {
			cursorManager.update(hoverCursor, cursor);
		} else {
			hoverCursor = cursorManager.add(cursor);
		}
	} else if (data.type === "leave") {
		hoverCursor && cursorManager.remove(hoverCursor);
		hoverCursor = null;
	}
}


export function isRouteActive() {
	return Mapy.getComponent("route").isActive() && !Mapy.getComponent("distanceMeter").isActive() && !Mapy.getComponent("userMarks").isActive();
}
